// Shared theme, status config, and time helpers for the Ops Board.
// Theme lifted verbatim from the school-ops-board.jsx prototype so the converted
// board keeps the prototype's visual language.

export const C = {
  bg: '#f0f4f8',
  surface: '#ffffff',
  surfaceAlt: '#f8fafc',
  surfaceHover: '#f1f5f9',
  border: '#e2e8f0',
  borderMed: '#cbd5e1',
  accent: '#0369a1',
  accentLight: '#e0f2fe',
  accentBorder: '#7dd3fc',
  green: '#15803d',
  greenLight: '#dcfce7',
  greenBorder: '#86efac',
  amber: '#b45309',
  amberLight: '#fef3c7',
  amberBorder: '#fcd34d',
  red: '#b91c1c',
  redLight: '#fee2e2',
  redBorder: '#fca5a5',
  purple: '#6d28d9',
  purpleLight: '#ede9fe',
  slate: '#475569',
  navy: '#0f2942',
  text: '#0f172a',
  textDim: '#475569',
  textMuted: '#94a3b8',
  pill: '#e2e8f0',
} as const;

export const F = "-apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif";
export const M = "'SFMono-Regular', Consolas, monospace";

// Booking lifecycle (booking_status). Prototype-only states (active/complete/
// no-show) are intentionally dropped — they arrive with FlightLog integration.
export const BOOKING_STATUS_CFG: Record<string, { color: string; bg: string; label: string; icon: string }> = {
  enquiry: { color: C.textMuted, bg: C.pill, label: 'Enquiry', icon: '◌' },
  quoted: { color: C.textMuted, bg: C.pill, label: 'Quoted', icon: '◌' },
  pending: { color: C.textMuted, bg: C.pill, label: 'Pending', icon: '◌' },
  confirmed: { color: C.green, bg: C.greenLight, label: 'Confirmed', icon: '●' },
  cancelled: { color: C.red, bg: C.redLight, label: 'Cancelled', icon: '✕' },
};

// Approval chip set (approval_status) — the §11.8 gate, orthogonal to booking_status.
export const APPROVAL_STATUS_CFG: Record<string, { color: string; bg: string; label: string; icon: string }> = {
  not_required: { color: C.green, bg: C.greenLight, label: 'Confirmed', icon: '●' },
  pending_staff: { color: C.amber, bg: C.amberLight, label: 'Awaiting instructor', icon: '⏳' },
  pending_asset: { color: C.amber, bg: C.amberLight, label: 'Awaiting asset owner', icon: '⏳' },
  pending_both: { color: C.amber, bg: C.amberLight, label: 'Awaiting approvals', icon: '⏳' },
  approved: { color: C.green, bg: C.greenLight, label: 'Approved', icon: '✓' },
  declined: { color: C.red, bg: C.redLight, label: 'Declined', icon: '✕' },
};

export const AVAIL_MODE_CFG: Record<string, { icon: string; color: string; label: string }> = {
  always: { icon: '', color: C.green, label: 'Always available' },
  restricted: { icon: '⚙', color: C.amber, label: 'Approval required' },
  conditional: { icon: '◈', color: C.purple, label: 'Check availability' },
  school_owned: { icon: '', color: C.green, label: 'School aircraft' },
  shared_charter: { icon: '🔗', color: C.amber, label: 'Charter priority' },
  third_party: { icon: '👤', color: C.purple, label: 'Owner approval' },
};

export function isPending(status: string): boolean {
  return status.startsWith('pending');
}

// ─── Time helpers (timezone-aware) ───────────────────────────────────────────
// Bookings store UTC; the board displays in the booking's dep_timezone (= base tz).

// Offset of `tz` from UTC, in minutes east of UTC, at the given instant.
function tzOffsetMinutes(date: Date, tz: string): number {
  const dtf = new Intl.DateTimeFormat('en-US', {
    timeZone: tz, hour12: false,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });
  const parts = dtf.formatToParts(date);
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  let hh = parseInt(map.hour, 10);
  if (hh === 24) hh = 0;
  const asUTC = Date.UTC(+map.year, +map.month - 1, +map.day, hh, +map.minute, +map.second);
  return (asUTC - date.getTime()) / 60000;
}

// HH:MM (24h) of a UTC ISO instant, rendered in `tz`.
export function fmtTimeInTz(iso: string, tz: string): string {
  return new Intl.DateTimeFormat('en-GB', {
    timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
  }).format(new Date(iso));
}

// Decimal hour-of-day (e.g. 9.5) of a UTC ISO instant, in `tz`.
export function hourOfDayInTz(iso: string, tz: string): number {
  const { hh, mm } = hmInTz(iso, tz);
  return hh + mm / 60;
}

function hmInTz(iso: string, tz: string): { hh: number; mm: number } {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: tz, hour: '2-digit', minute: '2-digit', hour12: false,
  }).formatToParts(new Date(iso));
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  let hh = parseInt(map.hour, 10);
  if (hh === 24) hh = 0;
  return { hh, mm: parseInt(map.minute, 10) };
}

// 'YYYY-MM-DD' of a UTC ISO instant, in `tz` (the local calendar day at the base).
export function dateKeyInTz(iso: string, tz: string): string {
  const parts = new Intl.DateTimeFormat('en-CA', {
    timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(new Date(iso));
  const map: Record<string, string> = {};
  for (const p of parts) map[p.type] = p.value;
  return `${map.year}-${map.month}-${map.day}`;
}

// UTC ISO instant for a wall-clock `dateStr` (YYYY-MM-DD) + `timeStr` (HH:MM) in `tz`.
// Used to send block_start_at to the API from the form's local date/time inputs.
export function localToUtcIso(dateStr: string, timeStr: string, tz: string): string {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const [h, mi] = timeStr.split(':').map(Number);
  // Two-pass: the offset can depend on the instant (DST). Converges for our zones.
  let utcMs = Date.UTC(y, mo - 1, d, h, mi, 0);
  for (let i = 0; i < 2; i++) {
    const off = tzOffsetMinutes(new Date(utcMs), tz);
    utcMs = Date.UTC(y, mo - 1, d, h, mi, 0) - off * 60000;
  }
  return new Date(utcMs).toISOString();
}

// UTC ISO bounds [from, to) for a calendar day `dateStr` (YYYY-MM-DD) in `tz`.
export function zonedDayRangeUtc(dateStr: string, tz: string): { from: string; to: string } {
  const from = localToUtcIso(dateStr, '00:00', tz);
  // 24h later in wall-clock terms — add a day to the date and take 00:00.
  const [y, mo, d] = dateStr.split('-').map(Number);
  const next = new Date(Date.UTC(y, mo - 1, d + 1));
  const nextStr = `${next.getUTCFullYear()}-${String(next.getUTCMonth() + 1).padStart(2, '0')}-${String(next.getUTCDate()).padStart(2, '0')}`;
  const to = localToUtcIso(nextStr, '00:00', tz);
  return { from, to };
}

// Add `days` to a YYYY-MM-DD string (calendar arithmetic, tz-agnostic).
export function addDays(dateStr: string, days: number): string {
  const [y, mo, d] = dateStr.split('-').map(Number);
  const dt = new Date(Date.UTC(y, mo - 1, d + days));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(dt.getUTCDate()).padStart(2, '0')}`;
}

// Short weekday label (Mon, Tue, …) for a YYYY-MM-DD.
export function weekdayLabel(dateStr: string): string {
  const [y, mo, d] = dateStr.split('-').map(Number);
  return new Intl.DateTimeFormat('en-AU', { weekday: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y, mo - 1, d)));
}

// "19 May" style label for a YYYY-MM-DD.
export function dayMonthLabel(dateStr: string): string {
  const [y, mo, d] = dateStr.split('-').map(Number);
  return new Intl.DateTimeFormat('en-AU', { day: 'numeric', month: 'short', timeZone: 'UTC' }).format(new Date(Date.UTC(y, mo - 1, d)));
}

// Today's date as YYYY-MM-DD in the given tz (board defaults to "today at base").
export function todayInTz(tz: string): string {
  return dateKeyInTz(new Date().toISOString(), tz);
}
